import { DataTable } from "@/components/data-table";
import { Tabs, TabsContent, TabsList } from "@/components/ui/tabs";
import { useEffect, useState } from "react";
import { columns } from "../_misc/columns";
import {
  useGetCardTransactions,
  useGetDeposits,
  useGetDepositsInfinite,
  useHasSwaps,
} from "@/hooks/use-queries";
import { useMediaQuery } from "@/hooks/use-media-query";
import TopBar from "@/components/topbar";
import { cardColumns } from "../_misc/cardColumns";
import TransactionList from "../_misc/TransactionList";
import SwapTab from "../_misc/swap/swap-tab";
import PillTabsTrigger from "@/components/pill-tabs-trigger";
import { useTabParam } from "@/hooks/use-tab-param";

type TransactionTab = "wallet" | "cards" | "swap";

const PAGE_SIZE = 10;

const Transactions = () => {
  const hasSwaps = useHasSwaps();
  const [tab, setTab] = useTabParam<TransactionTab>(
    "tab",
    "wallet",
    hasSwaps ? ["wallet", "cards", "swap"] : ["wallet", "cards"],
  );

  // One page state shared by both tabs, reset to the first page on tab change.
  // `pageIndex` is zero-based here, as the shared DataTable expects.
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  // The table (desktop) pages by number; the list (below lg) loads as it
  // scrolls. Only the visible one fetches.
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  // Mobile reveals the already-fetched card transactions PAGE_SIZE at a time.
  const [visibleCardCount, setVisibleCardCount] = useState(PAGE_SIZE);

  const { data: transactions, isLoading: isLoadingTransactions } =
    useGetDeposits(
      { page: pagination.pageIndex + 1, limit: pagination.pageSize },
      isDesktop,
    );
  const {
    data: walletPages,
    isLoading: isLoadingWalletList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetDepositsInfinite({ limit: PAGE_SIZE }, !isDesktop);
  // New transactions shift offset pages, so a row can repeat across pages.
  const walletListItems = [
    ...new Map(
      (walletPages?.pages ?? []).flatMap((p) => p.data).map((tx) => [tx.id, tx]),
    ).values(),
  ];

  const { data: cardTransactions, isLoading: isLoadingCardTransactions } =
    useGetCardTransactions({
      page: 0,
      limit: 150,
    });
  const cardRows = cardTransactions?.data ?? [];
  const cardPageItems = cardRows.slice(
    pagination.pageIndex * PAGE_SIZE,
    (pagination.pageIndex + 1) * PAGE_SIZE,
  );
  const cardListItems = cardRows.slice(0, visibleCardCount);
  const pageCount =
    tab === "cards"
      ? Math.max(1, Math.ceil(cardRows.length / PAGE_SIZE))
      : transactions?.totalPages || 1;

  const handleTabChange = (value: TransactionTab) => {
    setTab(value);
    setPagination({ pageIndex: 0, pageSize: PAGE_SIZE });
    setVisibleCardCount(PAGE_SIZE);
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  return (
    <div className="">
      <TopBar title="Transactions" />
      <Tabs
        onValueChange={(value) => {
          handleTabChange(value as TransactionTab);
        }}
        value={tab}
        className="font-poppins px-4 py-4 lg:bg-transparent"
      >
        <TabsList className="border-dark-stroke-5 bg-dark-background-secondary h-10 w-fit rounded-full border p-1">
          <PillTabsTrigger value="wallet" active={tab === "wallet"}>
            Wallet
          </PillTabsTrigger>
          <PillTabsTrigger value="cards" active={tab === "cards"}>
            Cards
          </PillTabsTrigger>
          {hasSwaps && (
            <PillTabsTrigger value="swap" active={tab === "swap"}>
              Swap
            </PillTabsTrigger>
          )}
        </TabsList>
        <div className="bg-dark-card-3 text-dark-text-50 mt-4 rounded-2xl p-4 backdrop-blur-sm lg:row-start-auto">
          <div className="flex items-center justify-between bg-white/5"></div>

          <TabsContent value="wallet" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={columns}
                data={transactions?.data ?? []}
                isLoading={isLoadingTransactions}
                pagination={pagination}
                setPagination={setPagination}
                pageCount={pageCount}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingWalletList}
                transactions={walletListItems}
                hasMore={hasNextPage}
                isFetchingMore={isFetchingNextPage}
                onLoadMore={() => {
                  if (!isFetchingNextPage) fetchNextPage();
                }}
              />
            </div>
          </TabsContent>
          <TabsContent value="cards" className="mt-2">
            <div className="hidden lg:block">
              <DataTable
                columns={cardColumns}
                data={cardPageItems}
                isLoading={isLoadingCardTransactions}
                pagination={pagination}
                setPagination={setPagination}
                pageCount={pageCount}
              />
            </div>
            <div className="lg:hidden">
              <TransactionList
                isLoading={isLoadingCardTransactions}
                transactions={cardListItems}
                type="cards"
                hasMore={visibleCardCount < cardRows.length}
                onLoadMore={() => setVisibleCardCount((c) => c + PAGE_SIZE)}
              />
            </div>
          </TabsContent>

          {hasSwaps && (
            <TabsContent value="swap" className="mt-2">
              <SwapTab />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </div>
  );
};

export default Transactions;
